import React, { useEffect, useCallback } from 'react';
import { FiBold, FiItalic, FiUnderline, FiAlignLeft, FiAlignCenter, FiAlignRight } from 'react-icons/fi';
import * as fabric from 'fabric';
import useEditorStore from '../../../stores/useEditorStore';
import { ColorPicker, SliderInput } from '../../common';
import { FONTS, FONT_SIZES, TEXT_PRESETS, DEFAULT_TEXT_INSET } from '../../../constants/editorConstants';
import { panelStyle, titleStyle, sectionStyle, labelStyle, selectStyle, inputStyle, styleButtonStyle } from '../../../styles/panelStyles';
import { fromFabricValue, toFabricValue, toFabricTextGradient, fromFabricTextGradient, isGradientConfig, isFabricGradient, hexToRgba, parseColorToHex, parseColorOpacity } from '../../../utils/gradientUtils';
import { getParagraphIndex, BULLET_PRESETS, NUMBER_PRESETS } from '../../../utils/bulletUtils';

const DEFAULT_TEXT_WIDTH = 300;
const DEFAULT_BG_COLOR = '#dbeafe';

// 재사용 가능한 토글 스위치 컴포넌트
const ToggleSwitch = ({ enabled, onChange, color = '#22c55e' }) => (
  <button
    onClick={onChange}
    style={{
      width: '48px', height: '26px', borderRadius: '13px', border: 'none',
      backgroundColor: enabled ? color : '#d1d5db',
      cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s',
    }}
  >
    <div style={{
      width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#ffffff',
      position: 'absolute', top: '3px', left: enabled ? '25px' : '3px',
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

const TextSettingsPanel = () => {
  const {
    canvas, canvasSize, selectedObjects, textSettings, setTextSettings, pushHistory, syncLayersFromCanvas, customFonts,
    selectedTableCells, selectedTableGroup,
  } = useEditorStore();

  // 기본 폰트 + 커스텀 폰트 합산 (중복 제거)
  const allFonts = React.useMemo(() => {
    const defaultValues = new Set(FONTS.map(f => f.value));
    const extra = customFonts.filter(f => !defaultValues.has(f.value));
    return [...extra, ...FONTS];
  }, [customFonts]);

  // TextBoxGroup 선택 시 내부 textbox를 selectedTextObject로 사용
  const selectedTextBoxGroup = selectedObjects.find(obj => obj.type === 'group' && obj._isTextBoxGroup);
  const selectedTextObject = selectedObjects.find(obj =>
    obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text'
  ) || (selectedTextBoxGroup ? selectedTextBoxGroup.getObjects().find(c => c.type === 'textbox' || c.type === 'i-text' || c.type === 'text') : null);

  // 테이블 그룹 감지 (selectedObjects에서 직접 선택되었거나 store의 selectedTableGroup)
  const selectedTable = selectedObjects.find(obj => obj.type === 'group' && obj._isTableGroup) || selectedTableGroup;
  // 테이블 셀 텍스트인지 판별
  const isTableCellText = !!(selectedTextObject?._isTableCellText && selectedTextObject?.group?._isTableGroup);

  // 테이블 모드: 테이블 전체 선택 또는 셀 선택 상태
  const isTableMode = !!(selectedTable || isTableCellText);

  // 테이블 모드에서 대상 텍스트 객체들 수집
  const getTableTargetTexts = React.useCallback(() => {
    const table = selectedTable || (isTableCellText ? selectedTextObject.group : null);
    if (!table) return [];
    const allTexts = table.getObjects().filter(c => c._isTableCellText);
    // 셀이 선택되어 있으면 해당 셀만, 아니면 전체
    if (selectedTableCells.length > 0) {
      const cellSet = new Set(selectedTableCells.map(c => `${c.row}_${c.col}`));
      return allTexts.filter(t => cellSet.has(`${t._cellRow}_${t._cellCol}`));
    }
    return allTexts;
  }, [selectedTable, isTableCellText, selectedTextObject, selectedTableCells]);

  // TextBoxGroup 배경색 감지 (직접 선택 또는 자식 textbox의 부모)
  const parentTextBoxGroup = selectedTextBoxGroup || (selectedTextObject?.group?._isTextBoxGroup ? selectedTextObject.group : null);
  const bgRect = parentTextBoxGroup?.getObjects().find(c => c.type === 'rect');

  // 텍스트 편집 중 선택 범위의 스타일 추출 헬퍼
  const extractSelectionSettings = useCallback((textObj) => {
    if (!textObj?.isEditing) return null;
    const start = textObj.selectionStart;
    const end = textObj.selectionEnd;
    if (start === end) {
      // 커서만 있을 때: 커서 위치의 스타일
      const styles = textObj.getSelectionStyles(start, start + 1);
      const s = styles[0] || {};
      return {
        fontFamily: s.fontFamily || textObj.fontFamily,
        fontSize: s.fontSize || textObj.fontSize,
        fontColor: s.fill || textObj.fill,
        bold: (s.fontWeight || textObj.fontWeight) === 'bold',
        italic: (s.fontStyle || textObj.fontStyle) === 'italic',
        underline: s.underline !== undefined ? s.underline : (textObj.underline || false),
      };
    }
    // 선택 범위: 첫 글자 기준
    const styles = textObj.getSelectionStyles(start, end);
    const first = styles[0] || {};
    return {
      fontFamily: first.fontFamily || textObj.fontFamily,
      fontSize: first.fontSize || textObj.fontSize,
      fontColor: first.fill || textObj.fill,
      bold: (first.fontWeight || textObj.fontWeight) === 'bold',
      italic: (first.fontStyle || textObj.fontStyle) === 'italic',
      underline: first.underline !== undefined ? first.underline : (textObj.underline || false),
    };
  }, []);

  // 텍스트 편집 중 선택 변경 시 툴바 동기화
  useEffect(() => {
    if (!selectedTextObject) return;

    const handleSelectionChanged = () => {
      const selSettings = extractSelectionSettings(selectedTextObject);
      if (selSettings) {
        setTextSettings(selSettings);
      }
    };

    selectedTextObject.on('selection:changed', handleSelectionChanged);
    return () => {
      selectedTextObject.off('selection:changed', handleSelectionChanged);
    };
  }, [selectedTextObject, extractSelectionSettings, setTextSettings]);

  // 텍스트 속성에서 설정값 추출 헬퍼
  const extractTextSettings = (textObj) => ({
    fontFamily: textObj.fontFamily || 'Noto Sans KR',
    fontSize: textObj.fontSize || 24,
    fontColor: textObj._gradientConfig
      || (isFabricGradient(textObj.fill)
        ? fromFabricTextGradient(textObj.fill, textObj)
        : (textObj.fill || '#000000')),
    bold: textObj.fontWeight === 'bold',
    italic: textObj.fontStyle === 'italic',
    underline: textObj.underline || false,
    align: textObj.textAlign || 'left',
    lineHeight: textObj.lineHeight || 1.3,
    charSpacing: textObj.charSpacing || 0,
    wordWrap: textObj.wordWrap || false,
    // Shadow
    shadowEnabled: !!textObj.shadow,
    shadowColor: textObj.shadow ? parseColorToHex(textObj.shadow.color) : '#000000',
    shadowOpacity: textObj.shadow ? Math.round(parseColorOpacity(textObj.shadow.color) * 100) : 50,
    shadowAngle: textObj.shadow
      ? ((Math.round(Math.atan2(textObj.shadow.offsetY, textObj.shadow.offsetX) * 180 / Math.PI) % 360) + 360) % 360
      : 45,
    shadowDist: textObj.shadow
      ? Math.round(Math.sqrt(textObj.shadow.offsetX ** 2 + textObj.shadow.offsetY ** 2))
      : 3,
    shadowBlur: textObj.shadow ? Math.round(textObj.shadow.blur || 0) : 4,
    // Outline
    outlineEnabled: !!textObj.stroke && (textObj.strokeWidth || 0) > 0,
    outlineColor: textObj.stroke ? parseColorToHex(textObj.stroke) : '#000000',
    outlineOpacity: textObj.stroke
      ? Math.round((typeof textObj.stroke === 'string' && textObj.stroke.startsWith('rgba') ? parseColorOpacity(textObj.stroke) : 1) * 100)
      : 100,
    outlineWidth: textObj.strokeWidth || 1,
  });

  useEffect(() => {
    if (selectedTextObject) {
      const settings = extractTextSettings(selectedTextObject);
      // Border (TextBoxGroup 전용 - 테이블은 표 탭에서 관리)
      if (!isTableCellText) {
        settings.borderEnabled = !!(parentTextBoxGroup?._borderColor);
        settings.borderColor = parentTextBoxGroup?._borderColor || '#000000';
        settings.borderOpacity = parentTextBoxGroup?._borderOpacity ?? 100;
        settings.borderWidth = parentTextBoxGroup?._borderWidth || 1;
      }
      // Background
      settings.bgEnabled = !!(parentTextBoxGroup && (parentTextBoxGroup._bgAlpha ?? 0) > 0);
      settings.bgColor = parentTextBoxGroup?._bgColor || DEFAULT_BG_COLOR;
      settings.bgAlpha = parentTextBoxGroup?._bgAlpha ?? 100;
      setTextSettings(settings);
    } else if (isTableMode) {
      // 테이블 전체 선택 시 (selectedTextObject가 null): 첫 번째 대상 셀의 속성으로 동기화
      const targets = getTableTargetTexts();
      if (targets.length > 0) {
        const firstText = targets[0];
        const settings = extractTextSettings(firstText);
        setTextSettings(settings);
      }
    }
  }, [selectedTextObject?.id, selectedTable?.id, selectedTableCells.length]);

  // TextBoxGroup 배경색 변경
  const handleBgColorChange = (color) => {
    if (!bgRect || !canvas) return;
    const alpha = (textSettings.bgAlpha ?? 100) / 100;
    const hex = parseColorToHex(color) || color;
    bgRect.set({ fill: hexToRgba(hex, alpha) });
    if (parentTextBoxGroup) parentTextBoxGroup._bgColor = hex;
    setTextSettings({ bgColor: hex });
    canvas.renderAll();
    pushHistory();
  };

  // TextBoxGroup 배경 투명도 변경
  const handleBgAlphaChange = (alpha) => {
    if (!bgRect || !canvas) return;
    const hex = textSettings.bgColor || parentTextBoxGroup?._bgColor || DEFAULT_BG_COLOR;
    bgRect.set({ fill: hexToRgba(hex, alpha / 100) });
    if (parentTextBoxGroup) parentTextBoxGroup._bgAlpha = alpha;
    setTextSettings({ bgAlpha: alpha });
    canvas.renderAll();
    pushHistory();
  };

  // 일반 텍스트박스를 TextBoxGroup으로 래핑 (배경 또는 테두리 추가 시 사용)
  const wrapTextboxInGroup = (textbox, options = {}) => {
    if (!canvas) return;
    const {
      bgColor = '#ffffff',
      bgAlpha = 0,
      borderColor = null,
      borderWidth = 0,
      borderOpacity = 100,
    } = options;

    const center = textbox.getCenterPoint();
    const angle = textbox.angle || 0;
    const marL = textbox._marL ?? DEFAULT_TEXT_INSET.L;
    const marR = textbox._marR ?? DEFAULT_TEXT_INSET.R;
    const marT = textbox._marT ?? DEFAULT_TEXT_INSET.T;
    const marB = textbox._marB ?? DEFAULT_TEXT_INSET.B;
    const textW = textbox.width * (textbox.scaleX || 1);
    const textH = textbox.height * (textbox.scaleY || 1);
    const groupW = textW + marL + marR;
    const groupH = textH + marT + marB;

    const bgFill = bgAlpha > 0 ? hexToRgba(bgColor, bgAlpha / 100) : 'rgba(255,255,255,0)';
    const strokeOpacity = borderOpacity / 100;
    const strokeColor = borderColor && borderWidth > 0
      ? (strokeOpacity < 1 ? hexToRgba(borderColor, strokeOpacity) : borderColor)
      : null;

    const newBgRect = new fabric.Rect({
      width: groupW, height: groupH,
      fill: bgFill, opacity: 1,
      originX: 'center', originY: 'center',
      selectable: false, evented: false,
      stroke: strokeColor, strokeWidth: borderColor ? borderWidth : 0,
    });

    textbox.set({
      left: (marL - marR) / 2,
      top: (marT - marB) / 2,
      originX: 'center', originY: 'center',
      scaleX: 1, scaleY: 1, angle: 0,
    });

    const group = new fabric.Group([newBgRect, textbox], {
      left: center.x, top: center.y,
      originX: 'center', originY: 'center',
      angle, subTargetCheck: false, interactive: false,
    });
    group.id = textbox.id || `textboxgroup-${Date.now()}`;
    group.name = textbox.name || 'Text';
    group._isTextBoxGroup = true;
    group._bgColor = bgColor;
    group._bgAlpha = bgAlpha;
    group._bgShape = 'rect';
    group._borderColor = borderColor;
    group._borderOpacity = borderOpacity;
    group._borderWidth = borderWidth;

    canvas.remove(textbox);
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  };

  // 배경색 토글
  const handleBgToggle = () => {
    if (!canvas || !selectedTextObject) return;
    const newEnabled = !textSettings.bgEnabled;
    const bgColor = textSettings.bgColor || DEFAULT_BG_COLOR;
    const bgAlpha = textSettings.bgAlpha ?? 100;
    setTextSettings({ bgEnabled: newEnabled });

    if (newEnabled) {
      if (parentTextBoxGroup && bgRect) {
        bgRect.set({ fill: hexToRgba(bgColor, bgAlpha / 100) });
        parentTextBoxGroup._bgColor = bgColor;
        parentTextBoxGroup._bgAlpha = bgAlpha;
        canvas.renderAll();
        pushHistory();
      } else {
        wrapTextboxInGroup(selectedTextObject, {
          bgColor, bgAlpha,
          borderColor: textSettings.borderEnabled ? (textSettings.borderColor || '#000000') : null,
          borderWidth: textSettings.borderEnabled ? (textSettings.borderWidth || 1) : 0,
          borderOpacity: textSettings.borderOpacity ?? 100,
        });
      }
    } else {
      if (parentTextBoxGroup && bgRect) {
        bgRect.set({ fill: 'rgba(255,255,255,0)' });
        parentTextBoxGroup._bgColor = '#ffffff';
        parentTextBoxGroup._bgAlpha = 0;
        canvas.renderAll();
        pushHistory();
      }
    }
  };

  // 테두리 토글 (TextBoxGroup 전용)
  const handleBorderToggle = () => {
    if (!canvas || !selectedTextObject) return;
    const newEnabled = !textSettings.borderEnabled;
    setTextSettings({ borderEnabled: newEnabled });

    if (newEnabled) {
      const color = textSettings.borderColor || '#000000';
      const opacity = (textSettings.borderOpacity ?? 100) / 100;
      const width = textSettings.borderWidth || 1;
      const strokeColor = opacity < 1 ? hexToRgba(color, opacity) : color;
      if (parentTextBoxGroup && bgRect) {
        // bgRect의 opacity를 1로 보장 (이전 버전 호환)
        if (bgRect.opacity === 0) {
          const curBgColor = parentTextBoxGroup._bgColor || '#ffffff';
          const curBgAlpha = (parentTextBoxGroup._bgAlpha ?? 0) / 100;
          bgRect.set({ fill: hexToRgba(curBgColor, curBgAlpha), opacity: 1 });
        }
        bgRect.set({ stroke: strokeColor, strokeWidth: width });
        parentTextBoxGroup._borderColor = color;
        parentTextBoxGroup._borderOpacity = textSettings.borderOpacity ?? 100;
        parentTextBoxGroup._borderWidth = width;
        canvas.renderAll();
        pushHistory();
      } else {
        wrapTextboxInGroup(selectedTextObject, {
          borderColor: color, borderWidth: width,
          borderOpacity: textSettings.borderOpacity ?? 100,
          bgColor: textSettings.bgColor || DEFAULT_BG_COLOR,
          bgAlpha: textSettings.bgEnabled ? (textSettings.bgAlpha ?? 100) : 0,
        });
      }
    } else {
      if (parentTextBoxGroup && bgRect) {
        bgRect.set({ stroke: null, strokeWidth: 0 });
        parentTextBoxGroup._borderColor = null;
        parentTextBoxGroup._borderOpacity = 100;
        parentTextBoxGroup._borderWidth = 0;
        canvas.renderAll();
        pushHistory();
      }
    }
  };

  // 테두리 색상 변경 (TextBoxGroup 전용)
  const handleBorderColorChange = (color) => {
    if (!canvas) return;
    setTextSettings({ borderColor: color });
    if (!parentTextBoxGroup || !bgRect) return;
    const opacity = (textSettings.borderOpacity ?? 100) / 100;
    bgRect.set({ stroke: opacity < 1 ? hexToRgba(color, opacity) : color });
    parentTextBoxGroup._borderColor = color;
    canvas.renderAll();
    pushHistory();
  };

  // 테두리 투명도 변경
  const handleBorderOpacityChange = (opacityVal) => {
    if (!parentTextBoxGroup || !bgRect || !canvas) return;
    const color = textSettings.borderColor || '#000000';
    const opacity = opacityVal / 100;
    bgRect.set({ stroke: opacity < 1 ? hexToRgba(color, opacity) : color });
    parentTextBoxGroup._borderOpacity = opacityVal;
    setTextSettings({ borderOpacity: opacityVal });
    canvas.renderAll();
    pushHistory();
  };

  // 테두리 두께 변경 (TextBoxGroup 전용)
  const handleBorderWidthChange = (width) => {
    if (!canvas) return;
    setTextSettings({ borderWidth: width });
    if (!parentTextBoxGroup || !bgRect) return;
    bgRect.set({ strokeWidth: width });
    parentTextBoxGroup._borderWidth = width;
    canvas.renderAll();
    pushHistory();
  };

  // 현재 커서 위치의 paragraph에 해당하는 글머리 타입 가져오기
  const getCurrentBullet = useCallback(() => {
    if (!selectedTextObject?._bullets) return null;
    const paraIdx = getParagraphIndex(selectedTextObject.text || '', selectedTextObject.selectionStart || 0);
    return selectedTextObject._bullets[paraIdx] || null;
  }, [selectedTextObject]);

  // 글머리 기호 설정
  // bulletSpec: null(해제), { type:'bullet', char } 또는 { type:'number', numType }
  const setBullets = useCallback((bulletSpec) => {
    if (!selectedTextObject || !canvas) return;
    if (!bulletSpec) {
      selectedTextObject._bullets = null;
    } else {
      const paraCount = (selectedTextObject.text || '').split('\n').length;
      const entry = bulletSpec.type === 'number'
        ? { type: 'number', numType: bulletSpec.numType || 'arabicPeriod', startAt: 1 }
        : { type: 'bullet', char: bulletSpec.char || '\u2022' };
      selectedTextObject._bullets = Array.from({ length: paraCount }, () => ({ ...entry }));
      // 글머리 기호 들여쓰기만큼 textbox 너비 확장
      const indent = Math.round(selectedTextObject.fontSize * 1.2);
      selectedTextObject.set('width', selectedTextObject.width + indent);
    }
    selectedTextObject.dirty = true;
    selectedTextObject.initDimensions();
    canvas.requestRenderAll();
    pushHistory();
  }, [selectedTextObject, canvas, pushHistory]);

  const handleWordWrapToggle = () => {
    const newWordWrap = !textSettings.wordWrap;
    setTextSettings({ wordWrap: newWordWrap });

    if (selectedTextObject && canvas) {
      selectedTextObject.wordWrap = newWordWrap;

      if (newWordWrap) {
        selectedTextObject.splitByGrapheme = true;
        selectedTextObject._wordJoiners = /[ \t\r]/;
      } else {
        selectedTextObject.splitByGrapheme = false;
        selectedTextObject._wordJoiners = /\x00/;
      }

      selectedTextObject.initDimensions();

      // 선택박스 색상 업데이트 (TextBoxGroup이면 그룹에, 아니면 텍스트 객체에)
      const controlColor = newWordWrap ? '#22c55e' : '#3b82f6';
      const colorTarget = parentTextBoxGroup || selectedTextObject;
      colorTarget.set({
        cornerColor: controlColor,
        cornerStrokeColor: '#ffffff',
        borderColor: controlColor,
      });

      canvas.renderAll();
      pushHistory();
    }
  };

  const addTextToCanvas = (preset) => {
    if (!canvas) return;
    const { fontFamily, fontColor, bold, italic, lineHeight, charSpacing, wordWrap = false } = textSettings;
    const BASE_CANVAS = 720;
    const scale = Math.min(canvasSize.width, canvasSize.height) / BASE_CANVAS;
    const baseFontSize = preset ? preset.fontSize : textSettings.fontSize;
    const fontSize = Math.round(baseFontSize * scale);
    const fontWeight = preset ? preset.fontWeight : (bold ? 'bold' : 'normal');
    const width = DEFAULT_TEXT_WIDTH;
    const cx = (canvasSize.width - width) / 2;
    const cy = (canvasSize.height - fontSize) / 2;
    const isWordWrap = wordWrap === true;

    const insetW = width - DEFAULT_TEXT_INSET.L - DEFAULT_TEXT_INSET.R;

    const newObject = new fabric.Textbox(preset ? `${preset.label} 텍스트` : '텍스트 입력', {
      left: cx,
      top: cy,
      originX: 'left',
      originY: 'top',
      fontFamily,
      fontSize,
      fill: toFabricValue(fontColor, insetW, fontSize),
      fontWeight,
      fontStyle: italic ? 'italic' : 'normal',
      lineHeight: lineHeight || 1.3,
      charSpacing: charSpacing || 0,
      width: insetW,
      wordWrap: isWordWrap,
      splitByGrapheme: isWordWrap,
      id: `text-${Date.now()}`,
      name: 'Text',
    });
    newObject._marL = DEFAULT_TEXT_INSET.L;
    newObject._marR = DEFAULT_TEXT_INSET.R;
    newObject._marT = DEFAULT_TEXT_INSET.T;
    newObject._marB = DEFAULT_TEXT_INSET.B;

    if (!isWordWrap) {
      newObject._wordJoiners = /\x00/;
      newObject.initDimensions();
    }

    // 그라데이션이면 실제 텍스트 너비에 맞게 재적용
    if (isGradientConfig(fontColor)) {
      newObject.set({ fill: toFabricTextGradient(fontColor, newObject) });
      newObject._gradientConfig = fontColor;
    }

    canvas.add(newObject);
    canvas.setActiveObject(newObject);
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  };

  // fabricSettings를 구성하는 헬퍼 (그라데이션 제외한 공통 속성)
  const buildFabricSettings = (newSettings) => {
    const fabricSettings = {};
    if (newSettings.fontFamily !== undefined) fabricSettings.fontFamily = newSettings.fontFamily;
    if (newSettings.fontSize !== undefined) fabricSettings.fontSize = Math.max(1, Math.min(300, newSettings.fontSize));
    if (newSettings.fontColor !== undefined && !isGradientConfig(newSettings.fontColor)) {
      fabricSettings.fill = newSettings.fontColor;
    }
    if (newSettings.bold !== undefined) fabricSettings.fontWeight = newSettings.bold ? 'bold' : 'normal';
    if (newSettings.italic !== undefined) fabricSettings.fontStyle = newSettings.italic ? 'italic' : 'normal';
    if (newSettings.underline !== undefined) fabricSettings.underline = newSettings.underline;
    if (newSettings.align !== undefined) fabricSettings.textAlign = newSettings.align;
    if (newSettings.lineHeight !== undefined) fabricSettings.lineHeight = newSettings.lineHeight;
    if (newSettings.charSpacing !== undefined) fabricSettings.charSpacing = newSettings.charSpacing;

    // Shadow 적용
    const shadowKeys = ['shadowEnabled', 'shadowColor', 'shadowOpacity', 'shadowAngle', 'shadowDist', 'shadowBlur'];
    if (shadowKeys.some(k => newSettings[k] !== undefined)) {
      const enabled = newSettings.shadowEnabled ?? textSettings.shadowEnabled;
      if (enabled) {
        const color = newSettings.shadowColor ?? textSettings.shadowColor;
        const opacity = (newSettings.shadowOpacity ?? textSettings.shadowOpacity) / 100;
        const angle = (newSettings.shadowAngle ?? textSettings.shadowAngle) * Math.PI / 180;
        const dist = newSettings.shadowDist ?? textSettings.shadowDist;
        const blur = newSettings.shadowBlur ?? textSettings.shadowBlur;
        fabricSettings.shadow = new fabric.Shadow({
          color: hexToRgba(color, opacity),
          blur,
          offsetX: Math.round(Math.cos(angle) * dist * 100) / 100,
          offsetY: Math.round(Math.sin(angle) * dist * 100) / 100,
        });
      } else {
        fabricSettings.shadow = null;
      }
    }

    // Outline 적용
    const outlineKeys = ['outlineEnabled', 'outlineColor', 'outlineOpacity', 'outlineWidth'];
    if (outlineKeys.some(k => newSettings[k] !== undefined)) {
      const enabled = newSettings.outlineEnabled ?? textSettings.outlineEnabled;
      if (enabled) {
        const color = newSettings.outlineColor ?? textSettings.outlineColor;
        const opacity = (newSettings.outlineOpacity ?? textSettings.outlineOpacity) / 100;
        const width = newSettings.outlineWidth ?? textSettings.outlineWidth;
        fabricSettings.stroke = opacity < 1 ? hexToRgba(color, opacity) : color;
        fabricSettings.strokeWidth = width;
        fabricSettings.paintFirst = 'stroke';
      } else {
        fabricSettings.stroke = null;
        fabricSettings.strokeWidth = 0;
      }
    }
    return fabricSettings;
  };

  const handleSettingChange = (newSettings) => {
    setTextSettings(newSettings);
    if (!canvas) return;

    // 테이블 모드: 대상 셀 텍스트들에 일괄 적용
    if (isTableMode && !selectedTextObject) {
      const targets = getTableTargetTexts();
      if (targets.length === 0) return;
      const fabricSettings = buildFabricSettings(newSettings);
      // 그라데이션은 테이블 셀에 적용하지 않음 (개별 텍스트 크기가 달라 의미 없음)
      targets.forEach(t => {
        if (newSettings.fontColor !== undefined && !isGradientConfig(newSettings.fontColor)) {
          delete t._gradientConfig;
        }
        t.set(fabricSettings);
      });
      canvas.renderAll();
      pushHistory();
      return;
    }

    // 단일 텍스트 객체 모드
    if (selectedTextObject) {
      const fabricSettings = buildFabricSettings(newSettings);
      // 그라데이션 처리 (단일 텍스트 전용)
      if (newSettings.fontColor !== undefined) {
        if (isGradientConfig(newSettings.fontColor)) {
          fabricSettings.fill = toFabricTextGradient(newSettings.fontColor, selectedTextObject);
          selectedTextObject._gradientConfig = newSettings.fontColor;
        } else {
          delete selectedTextObject._gradientConfig;
        }
      }

      // 텍스트 편집 중이고 선택 범위가 있으면 → 선택 범위에만 글자 스타일 적용
      const isEditing = selectedTextObject.isEditing;
      const hasSelection = isEditing && (selectedTextObject.selectionStart !== selectedTextObject.selectionEnd);
      // 글자 단위 속성 (선택 범위에 적용 가능)
      const charStyleKeys = ['fontFamily', 'fontSize', 'fill', 'fontWeight', 'fontStyle', 'underline'];

      if (hasSelection) {
        // 선택 범위에 적용할 글자 스타일만 추출
        const selStyles = {};
        for (const key of charStyleKeys) {
          if (fabricSettings[key] !== undefined) selStyles[key] = fabricSettings[key];
        }
        if (Object.keys(selStyles).length > 0) {
          selectedTextObject.setSelectionStyles(selStyles);
          selectedTextObject.dirty = true;
          selectedTextObject.initDimensions();
        }
        // 비-글자 속성 (정렬, 그림자, 외곽선 등)은 객체 전체에 적용
        const objSettings = {};
        for (const [k, v] of Object.entries(fabricSettings)) {
          if (!charStyleKeys.includes(k)) objSettings[k] = v;
        }
        if (Object.keys(objSettings).length > 0) {
          selectedTextObject.set(objSettings);
        }
      } else if (isTableCellText && selectedTableCells.length > 1) {
        // 테이블 셀 텍스트 + 다중 셀 선택 시 선택된 셀 전체에 적용
        const targets = getTableTargetTexts();
        targets.forEach(t => {
          if (newSettings.fontColor !== undefined && !isGradientConfig(newSettings.fontColor)) {
            delete t._gradientConfig;
          }
          t.set(fabricSettings);
        });
      } else {
        selectedTextObject.set(fabricSettings);
        // 선택 없이 변경 시, per-character 스타일도 함께 업데이트
        // (붙여넣기 등으로 글자별 개별 스타일이 설정된 경우 base 속성만으론 반영 안 됨)
        const text = selectedTextObject.text || '';
        if (text.length > 0) {
          const charStyles = {};
          for (const key of charStyleKeys) {
            if (fabricSettings[key] !== undefined) charStyles[key] = fabricSettings[key];
          }
          if (Object.keys(charStyles).length > 0) {
            const savedStart = selectedTextObject.selectionStart;
            const savedEnd = selectedTextObject.selectionEnd;
            selectedTextObject.selectionStart = 0;
            selectedTextObject.selectionEnd = text.length;
            selectedTextObject.setSelectionStyles(charStyles);
            selectedTextObject.selectionStart = savedStart;
            selectedTextObject.selectionEnd = savedEnd;
            selectedTextObject.dirty = true;
            selectedTextObject.initDimensions();
          }
        }
      }
      canvas.renderAll();
      pushHistory();
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>{(selectedTextObject || isTableMode) ? '텍스트 설정' : '텍스트 추가'}</h3>

      {/* 텍스트 프리셋 (추가 모드에서만 표시) */}
      {!selectedTextObject && !isTableMode && (
        <div style={{ ...sectionStyle, marginTop: '20px' }}>
          <label style={{ ...labelStyle, fontSize: '15px' }}>텍스트 종류</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {TEXT_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => addTextToCanvas(preset)}
                style={{
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ fontSize: `${preset.displaySize}px`, fontWeight: preset.fontWeight }}>
                  {preset.label} 텍스트 추가
                </span>
                <span style={{ fontSize: '20px', color: '#9ca3af' }}>+</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 폰트 선택 */}
      <div style={{ ...sectionStyle, marginTop: selectedTextObject ? '20px' : undefined }}>
        <label style={labelStyle}>폰트</label>
        <select
          value={textSettings.fontFamily}
          onChange={(e) => handleSettingChange({ fontFamily: e.target.value })}
          style={selectStyle}
        >
          {allFonts.map((font) => (
            <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
              {font.label}
            </option>
          ))}
        </select>
      </div>

      {/* 폰트 크기 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>크기</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={textSettings.fontSize}
            onChange={(e) => handleSettingChange({ fontSize: parseInt(e.target.value) })}
            style={{ ...selectStyle, flex: 1 }}
          >
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>{size}px</option>
            ))}
          </select>
          <input
            type="number"
            value={textSettings.fontSize}
            onChange={(e) => handleSettingChange({ fontSize: Math.max(1, Math.min(300, parseInt(e.target.value) || 16)) })}
            min="1"
            max="300"
            style={{ ...inputStyle, width: '80px' }}
          />
        </div>
      </div>

      {/* 텍스트 색상 */}
      <div style={sectionStyle}>
        <ColorPicker
          label="색상"
          value={textSettings.fontColor}
          onChange={(fontColor) => handleSettingChange({ fontColor })}
          showGradient
          size="large"
        />
      </div>

      {/* 배경색 (테이블 모드에서는 표시 안함) */}
      {!isTableMode && (selectedTextObject || parentTextBoxGroup) && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>배경색</label>
            <ToggleSwitch enabled={textSettings.bgEnabled} onChange={handleBgToggle} color="#3b82f6" />
          </div>
          {textSettings.bgEnabled && parentTextBoxGroup && bgRect && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <ColorPicker
                label="색상"
                value={textSettings.bgColor || DEFAULT_BG_COLOR}
                onChange={handleBgColorChange}
                showTransparent
                size="large"
              />
              <SliderInput
                label="투명도"
                value={textSettings.bgAlpha ?? 100}
                onChange={handleBgAlphaChange}
                min={0}
                max={100}
                step={1}
                unit="%"
              />
            </div>
          )}
        </div>
      )}

      {/* 스타일 버튼 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>스타일</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => handleSettingChange({ bold: !textSettings.bold })}
            style={{
              ...styleButtonStyle,
              backgroundColor: textSettings.bold ? '#3b82f6' : '#f3f4f6',
              color: textSettings.bold ? '#ffffff' : '#374151',
            }}
          >
            <FiBold size={18} />
          </button>
          <button
            onClick={() => handleSettingChange({ italic: !textSettings.italic })}
            style={{
              ...styleButtonStyle,
              backgroundColor: textSettings.italic ? '#3b82f6' : '#f3f4f6',
              color: textSettings.italic ? '#ffffff' : '#374151',
            }}
          >
            <FiItalic size={18} />
          </button>
          <button
            onClick={() => handleSettingChange({ underline: !textSettings.underline })}
            style={{
              ...styleButtonStyle,
              backgroundColor: textSettings.underline ? '#3b82f6' : '#f3f4f6',
              color: textSettings.underline ? '#ffffff' : '#374151',
            }}
          >
            <FiUnderline size={18} />
          </button>
        </div>
      </div>

      {/* 정렬 */}
      <div style={sectionStyle}>
        <label style={labelStyle}>정렬</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'left', icon: FiAlignLeft },
            { id: 'center', icon: FiAlignCenter },
            { id: 'right', icon: FiAlignRight },
          ].map(({ id, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleSettingChange({ align: id })}
              style={{
                ...styleButtonStyle,
                backgroundColor: textSettings.align === id ? '#3b82f6' : '#f3f4f6',
                color: textSettings.align === id ? '#ffffff' : '#374151',
              }}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>

      {/* 글머리 기호 */}
      {(selectedTextObject || isTableMode) && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>글머리 기호</label>
            <ToggleSwitch
              enabled={!!(selectedTextObject?._bullets)}
              onChange={() => setBullets(selectedTextObject?._bullets ? null : { type: 'bullet', char: '\u2022' })}
              color="#8b5cf6"
            />
          </div>
          {selectedTextObject?._bullets && (
            <div style={{ marginTop: '8px' }}>
              <select
                value={(() => {
                  const b = getCurrentBullet();
                  if (!b) return 'bullet:•';
                  if (b.type === 'bullet') return `bullet:${b.char || '•'}`;
                  return `number:${b.numType || 'arabicPeriod'}`;
                })()}
                onChange={(e) => {
                  const [type, sub] = e.target.value.split(':');
                  if (type === 'bullet') setBullets({ type: 'bullet', char: sub });
                  else setBullets({ type: 'number', numType: sub });
                }}
                style={selectStyle}
              >
                <optgroup label="문자 글머리">
                  {BULLET_PRESETS.map(p => (
                    <option key={p.id} value={`bullet:${p.char}`}>{p.label}</option>
                  ))}
                </optgroup>
                <optgroup label="번호 글머리">
                  {NUMBER_PRESETS.map(p => (
                    <option key={p.id} value={`number:${p.id}`}>{p.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}
        </div>
      )}

      {/* 자동 줄바꿈 토글 */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>자동 줄바꿈</label>
          <ToggleSwitch enabled={textSettings.wordWrap} onChange={handleWordWrapToggle} />
        </div>
      </div>

      <SliderInput
        label="줄간격"
        value={textSettings.lineHeight || 1.3}
        onChange={(lineHeight) => handleSettingChange({ lineHeight })}
        min={0.8}
        max={3}
        step={0.1}
      />

      <SliderInput
        label="자간"
        value={textSettings.charSpacing || 0}
        onChange={(charSpacing) => handleSettingChange({ charSpacing })}
        min={-100}
        max={500}
        step={10}
      />

      {/* 그림자 섹션 (테이블 셀 텍스트는 그림자 미지원) */}
      {!isTableCellText && (
      <div style={{ ...sectionStyle, borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>그림자</label>
          <ToggleSwitch enabled={textSettings.shadowEnabled} onChange={() => handleSettingChange({ shadowEnabled: !textSettings.shadowEnabled })} />
        </div>
        {textSettings.shadowEnabled && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <ColorPicker
              label="색상"
              value={textSettings.shadowColor}
              onChange={(shadowColor) => handleSettingChange({ shadowColor })}
              size="large"
            />
            <SliderInput
              label="투명도"
              value={textSettings.shadowOpacity}
              onChange={(shadowOpacity) => handleSettingChange({ shadowOpacity })}
              min={0}
              max={100}
              step={1}
              unit="%"
            />
            <SliderInput
              label="각도"
              value={textSettings.shadowAngle}
              onChange={(shadowAngle) => handleSettingChange({ shadowAngle })}
              min={0}
              max={360}
              step={1}
              unit="°"
            />
            <SliderInput
              label="거리"
              value={textSettings.shadowDist}
              onChange={(shadowDist) => handleSettingChange({ shadowDist })}
              min={0}
              max={20}
              step={1}
              unit="px"
            />
            <SliderInput
              label="블러"
              value={textSettings.shadowBlur}
              onChange={(shadowBlur) => handleSettingChange({ shadowBlur })}
              min={0}
              max={20}
              step={1}
              unit="px"
            />
          </div>
        )}
      </div>
      )}

      {/* 외곽선 섹션 */}
      <div style={{ ...sectionStyle, borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>외곽선</label>
          <ToggleSwitch enabled={textSettings.outlineEnabled} onChange={() => handleSettingChange({ outlineEnabled: !textSettings.outlineEnabled })} />
        </div>
        {textSettings.outlineEnabled && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <ColorPicker
              label="색상"
              value={textSettings.outlineColor}
              onChange={(outlineColor) => handleSettingChange({ outlineColor })}
              size="large"
            />
            <SliderInput
              label="투명도"
              value={textSettings.outlineOpacity}
              onChange={(outlineOpacity) => handleSettingChange({ outlineOpacity })}
              min={0}
              max={100}
              step={1}
              unit="%"
            />
            <SliderInput
              label="두께"
              value={textSettings.outlineWidth}
              onChange={(outlineWidth) => handleSettingChange({ outlineWidth })}
              min={1}
              max={10}
              step={1}
              unit="px"
            />
          </div>
        )}
      </div>

      {/* 테두리 (TextBoxGroup 전용 - 테이블은 표 탭에서 변경) */}
      {!isTableMode && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={labelStyle}>테두리</span>
            <ToggleSwitch enabled={textSettings.borderEnabled} onChange={handleBorderToggle} color="#3b82f6" />
          </div>
          {textSettings.borderEnabled && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <ColorPicker
                label="색상"
                value={textSettings.borderColor}
                onChange={handleBorderColorChange}
                size="large"
              />
              <SliderInput
                label="투명도"
                value={textSettings.borderOpacity ?? 100}
                onChange={handleBorderOpacityChange}
                min={0}
                max={100}
                step={1}
                unit="%"
              />
              <SliderInput
                label="두께"
                value={textSettings.borderWidth}
                onChange={handleBorderWidthChange}
                min={1}
                max={10}
                step={1}
                unit="px"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TextSettingsPanel;
